/**
 * Pure "Keep the Change" round-up calculation — no jQuery, no DOM, no side
 * effects, so it can be reasoned about and unit-tested in isolation. The DOM
 * wiring lives in roundup.ts, which reads the page, calls this, and writes the
 * basket.
 *
 * Mirrors the reference behaviour in lottery-royal-marsden's public/js/roundup.js
 * with two deliberate hardenings that fix the live bug on cancer-wales:
 *   - a missing/blank round-up value never escapes as the string "NaN" (blank
 *     suggestion, 0.00 donation, total = monthly);
 *   - all money is formatted to 2 decimal places, matching the server-rendered
 *     values.
 *
 * Monthly cost is `entries × perTicketPrice` — there is NO ×4 factor;
 * per_ticket_price is already the monthly price of one weekly entry (this matches
 * AbstractPlayController::calculateMonthlyPrice() and the rendered "£x per month"
 * entry labels).
 */

export interface RoundupCalcInput {
    /** Number of weekly entries chosen; 0 = none selected. */
    entries: number;
    /** Monthly price of a single weekly entry (#per_ticket_price). */
    perTicketPrice: number;
    /** The lottery's suggested round-up amounts [value_1..value_5]; null/NaN = unset. */
    roundupValues: ReadonlyArray<number | null>;
    /** Whether the "Yes, keep the change" radio is selected. */
    roundupSelected: boolean;
    /** Raw contents of the #roundup_amount field (what the supporter will donate). */
    enteredAmount: string | number | null;
    /** Gift Aid: true = "Yes", false = "No", null = not yet answered. */
    giftAid: boolean | null;
}

export interface RoundupCalcResult {
    entries: number;
    /** (entries × perTicketPrice), 2dp. */
    monthlyCost: string;
    /**
     * The amount to offer in the #roundup_amount field: 2dp of the value for this
     * entry count (index clamped to 5). Empty when round-up is not selected, no
     * entries are chosen, or the lottery has no value for that count.
     */
    suggestedRoundup: string;
    /** The effective donation from the entered amount, 2dp; '0.00' when absent/invalid. */
    roundupAmount: string;
    /** Basket donation cell = roundupAmount. */
    donation: string;
    /** monthlyCost + donation, 2dp. */
    total: string;
    /** Empty while the question is unanswered — the basket must not presume "No". */
    giftAidLabel: 'Yes' | 'No' | '';
    /** Show the basket at all (entries chosen). */
    showBasket: boolean;
    /** Show the donation / total rows (round-up selected). */
    showRoundupRows: boolean;
    /** Show the Gift Aid row: round-up selected AND the question answered either way. */
    showGiftAidRow: boolean;
}

/** Coerce to a finite number or NaN (blank/empty/"NaN"/null all become NaN). */
function toNumber(value: string | number | null | undefined): number {
    if (value === null || value === undefined || value === '') {
        return NaN;
    }

    return parseFloat(String(value));
}

/** Format money to 2dp, treating anything non-finite as 0. */
function money(value: number): string {
    return (Number.isFinite(value) ? value : 0).toFixed(2);
}

export function calculateRoundup(input: RoundupCalcInput): RoundupCalcResult {
    const entries = Number.isFinite(input.entries) && input.entries > 0 ? Math.floor(input.entries) : 0;
    const perTicketPrice = Number.isFinite(input.perTicketPrice) ? input.perTicketPrice : 0;

    const monthlyCostNum = entries * perTicketPrice;

    // Suggested round-up for this entry count (index clamped to the 5 configured
    // values). Only when round-up is selected and the value is actually set.
    let suggestedRoundup = '';
    if (input.roundupSelected && entries > 0) {
        const raw = input.roundupValues[Math.min(entries, 5) - 1];
        const value = typeof raw === 'number' ? raw : NaN;
        if (Number.isFinite(value)) {
            suggestedRoundup = money(value);
        }
    }

    // The donation actually applied comes from the field's current contents (which
    // may be the suggestion or a supporter-edited value). Ignored when not opting in.
    let donationNum = 0;
    if (input.roundupSelected) {
        const entered = toNumber(input.enteredAmount);
        if (Number.isFinite(entered)) {
            donationNum = entered;
        }
    }
    const donation = money(donationNum);

    return {
        entries,
        monthlyCost: money(monthlyCostNum),
        suggestedRoundup,
        roundupAmount: donation,
        donation,
        total: money(monthlyCostNum + donationNum),
        giftAidLabel: input.giftAid === null ? '' : input.giftAid ? 'Yes' : 'No',
        showBasket: entries > 0,
        showRoundupRows: input.roundupSelected,
        showGiftAidRow: input.roundupSelected && input.giftAid !== null,
    };
}
